import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Phone,
  Lock,
  Edit2,
  Plus,
  Trash2,
  Package,
  Heart,
  Award,
  Calendar,
  ChevronRight,
  Home,
  Briefcase,
  MapPin,
  Check,
  X
} from 'lucide-react';
import { fetchUserAddressesFromBackend, addUserAddressBackend, BACKEND_URL, getProductImageUrl } from '../services/api';

const UserProfile = () => {
  const navigate = useNavigate();

  // User Auth & Info State
  const userJson = localStorage.getItem('user');
  const userToken = localStorage.getItem('userToken');
  let user = null;
  try {
    if (userJson) user = JSON.parse(userJson);
  } catch (e) {}

  useEffect(() => {
    if (!userToken && (!user || user.role === 'admin')) {
      navigate('/login');
    }
  }, [userToken, user, navigate]);

  if (!user) {
    user = { name: '', email: '', phone: '' };
  }

  // Edit Mode State
  const [isEditingPersonal, setIsEditingPersonal] = useState(false);
  const [fullName, setFullName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [phoneCountry, setPhoneCountry] = useState('+44');
  const [phoneNumber, setPhoneNumber] = useState(user.phone || '');

  // Password Modal/Change State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPass, setCurrentPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');

  // Addresses State
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [newAddr, setNewAddr] = useState({
    label: 'Home',
    full_name: user.name || '',
    phone: '+44 7123 456789',
    street_address: '',
    city: '',
    postal_code: '',
    country: 'United Kingdom'
  });

  // Recent Orders & Stats State
  const [orders, setOrders] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);

  // Initial Real Dynamic Data Loading
  useEffect(() => {
    let isMounted = true;

    // Load Addresses
    const loadAddresses = async () => {
      const addrs = await fetchUserAddressesFromBackend();
      if (isMounted) {
        setSavedAddresses(addrs && addrs.length > 0 ? addrs : []);
        setLoadingAddresses(false);
      }
    };

    // Load Real Orders from API
    const loadOrders = async () => {
      try {
        const token = localStorage.getItem('userToken') || localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/orders`, {
          headers: { 'Authorization': token ? `Bearer ${token}` : '' }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && isMounted && Array.isArray(data.orders)) {
            setOrders(data.orders);
          }
        }
      } catch (e) {}
    };

    // Load Real Wishlist Count from API
    const loadWishlist = async () => {
      try {
        const token = localStorage.getItem('userToken') || localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/wishlist`, {
          headers: { 'Authorization': token ? `Bearer ${token}` : '' }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && isMounted && Array.isArray(data.wishlist)) {
            setWishlistCount(data.wishlist.length);
          }
        }
      } catch (e) {}
    };

    loadAddresses();
    loadOrders();
    loadWishlist();
    return () => { isMounted = false; };
  }, []);

  const fallbackRecentOrders = [
    { id: 'IR24351', order_number: '#IR24351', created_at: 'May 16, 2024', items_count: 2, order_status: 'Delivered', total_amount: 1049.00, image_url: getProductImageUrl('/uploads/panel_144led_pro.jpg') },
    { id: 'IR24122', order_number: '#IR24122', created_at: 'Apr 28, 2024', items_count: 1, order_status: 'Delivered', total_amount: 350.00, image_url: getProductImageUrl('/uploads/panel_72led.jpg') },
    { id: 'IR23875', order_number: '#IR23875', created_at: 'Apr 12, 2024', items_count: 1, order_status: 'Shipped', total_amount: 950.00, image_url: getProductImageUrl('/uploads/panel_144led.jpg') },
    { id: 'IR23650', order_number: '#IR23650', created_at: 'Mar 30, 2024', items_count: 2, order_status: 'Processing', total_amount: 98.00, image_url: getProductImageUrl('/uploads/panel_72led.jpg') }
  ];

  // Save Personal Info
  const handleSavePersonal = (e) => {
    e.preventDefault();
    const updatedUser = { ...user, name: fullName, email };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setIsEditingPersonal(false);
  };

  // Add Address Handler
  const handleAddAddressSubmit = async (e) => {
    e.preventDefault();
    if (!newAddr.full_name || !newAddr.street_address) return;
    const created = await addUserAddressBackend(newAddr);
    if (created && created.address) {
      setSavedAddresses(prev => [...prev, created.address]);
    } else {
      setSavedAddresses(prev => [...prev, { ...newAddr, id: `addr-${Date.now()}` }]);
    }
    setShowAddAddressModal(false);
    setNewAddr({ label: 'Home', full_name: fullName, phone: '+44 7123 456789', street_address: '', city: '', postal_code: '', country: 'United Kingdom' });
  };

  // Delete Address Handler
  const handleDeleteAddress = (id) => {
    if (window.confirm("Are you sure you want to delete this address?")) {
      setSavedAddresses(prev => prev.filter(a => a.id !== id));
    }
  };

  // Status Pill Renderer matching screenshot
  const renderStatusPill = (status) => {
    const s = status ? status.toLowerCase() : 'processing';
    let bg = '#dcfce7';
    let color = '#15803d';

    if (s === 'shipped') {
      bg = '#eff6ff';
      color = '#1d4ed8';
    } else if (s === 'processing') {
      bg = '#fff7ed';
      color = '#c2410c';
    } else if (s === 'cancelled') {
      bg = '#fef2f2';
      color = '#b91c1c';
    }

    return (
      <span style={{ fontSize: '10.5px', fontWeight: 800, color, background: bg, padding: '2px 8px', borderRadius: '9999px', display: 'inline-block' }}>
        {status}
      </span>
    );
  };

  return (
    <div className="profile-page-container" style={{ minHeight: '100vh', background: '#fafbfc', padding: '40px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        
        {/* HEADER TITLE */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: 900, color: '#0f172a', margin: '0 0 4px 0', letterSpacing: '-0.02em' }}>
            My Profile
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
            Manage your personal information and account details.
          </p>
        </div>

        {/* 2-COLUMN LAYOUT: LEFT (PERSONAL INFO + ADDRESSES) vs RIGHT (ACCOUNT OVERVIEW + RECENT ORDERS) */}
        <div className="cart-page-grid" style={{ gridTemplateColumns: '1fr 440px', gap: '32px' }}>
          
          {/* LEFT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', minWidth: 0 }}>
            
            {/* 1. PERSONAL INFORMATION CARD MATCHING PROFILE.PNG */}
            <div className="detail-card-padded" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '32px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: 0 }}>
                  Personal Information
                </h2>

                <button
                  onClick={() => setIsEditingPersonal(!isEditingPersonal)}
                  style={{
                    background: '#dc2626',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '8px 18px',
                    fontSize: '13px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)'
                  }}
                >
                  <Edit2 size={14} />
                  <span>{isEditingPersonal ? 'Cancel' : 'Edit Profile'}</span>
                </button>
              </div>

              {isEditingPersonal ? (
                <form onSubmit={handleSavePersonal} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div className="profile-info-grid">
                    <div>
                      <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Full Name</label>
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Email Address</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="submit" style={{ background: '#dc2626', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '9px 22px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>
                      Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* Row 1: Full Name & Email Address */}
                  <div className="profile-info-grid">
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Full Name</div>
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '11px 14px', fontSize: '13.5px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px', wordBreak: 'break-all' }}>
                        <User size={16} color="#94a3b8" style={{ flexShrink: 0 }} />
                        <span style={{ fontWeight: 600 }}>{fullName}</span>
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Email Address</div>
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '11px 14px', fontSize: '13.5px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px', wordBreak: 'break-all' }}>
                        <Mail size={16} color="#94a3b8" style={{ flexShrink: 0 }} />
                        <span style={{ fontWeight: 600 }}>{email}</span>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Mobile Number & Password */}
                  <div className="profile-info-grid">
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Mobile Number</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <select
                          value={phoneCountry}
                          onChange={(e) => setPhoneCountry(e.target.value)}
                          style={{ width: '80px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '11px 8px', fontSize: '13px', fontWeight: 700, outline: 'none' }}
                        >
                          <option value="+44">+44</option>
                          <option value="+1">+1</option>
                          <option value="+33">+33</option>
                          <option value="+49">+49</option>
                        </select>
                        <div style={{ flex: 1, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '11px 14px', fontSize: '13.5px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Phone size={16} color="#94a3b8" style={{ flexShrink: 0 }} />
                          <span style={{ fontWeight: 600 }}>{phoneNumber}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Password</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ flex: 1, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '11px 14px', fontSize: '13.5px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Lock size={16} color="#94a3b8" style={{ flexShrink: 0 }} />
                          <span style={{ letterSpacing: '2px', fontWeight: 800 }}>••••••••</span>
                        </div>
                        <button
                          onClick={() => setShowPasswordModal(true)}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #cbd5e1',
                            borderRadius: '12px',
                            padding: '0 16px',
                            fontSize: '13px',
                            fontWeight: 700,
                            color: '#334155',
                            cursor: 'pointer'
                          }}
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              )}

            </div>

            {/* 2. SAVED ADDRESSES SECTION MATCHING PROFILE.PNG */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: '0 0 2px 0' }}>
                    Saved Addresses
                  </h2>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                    Manage your delivery addresses for a faster checkout.
                  </p>
                </div>

                <button
                  onClick={() => setShowAddAddressModal(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#dc2626',
                    fontSize: '13.5px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Plus size={16} />
                  <span>Add New Address</span>
                </button>
              </div>

              {/* ADDRESS CARDS LIST */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {savedAddresses.length === 0 ? (
                  <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '32px', textAlign: 'center', color: '#64748b', fontSize: '14px', fontWeight: 700 }}>
                    No saved addresses
                  </div>
                ) : (
                  savedAddresses.map((addr) => (
                    <div
                      key={addr.id}
                      className="saved-address-card detail-card-padded"
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '20px',
                        padding: '24px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                      }}
                    >
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {addr.label?.toLowerCase() === 'work' ? (
                            <Briefcase size={20} color="#dc2626" />
                          ) : addr.label?.toLowerCase().includes('parent') ? (
                            <MapPin size={20} color="#dc2626" />
                          ) : (
                            <Home size={20} color="#dc2626" />
                          )}
                        </div>

                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 900, color: '#0f172a' }}>{addr.label || 'Home'}</span>
                            {addr.is_default && (
                              <span style={{ fontSize: '10px', fontWeight: 800, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', padding: '1px 8px', borderRadius: '4px' }}>
                                Default
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '14px', fontWeight: 900, color: '#0f172a', marginBottom: '4px' }}>{addr.full_name || addr.name}</div>
                          <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.4, maxWidth: '380px', wordBreak: 'break-word' }}>
                            {addr.street_address || addr.street}, {addr.city}, {addr.postal_code || addr.postcode}, {addr.country}
                          </div>
                          <div style={{ fontSize: '12.5px', color: '#64748b', marginTop: '6px' }}>{addr.phone}</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <button
                          onClick={() => handleDeleteAddress(addr.id)}
                          style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '13px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        >
                          <Trash2 size={14} />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

            </div>

          </div>

          {/* RIGHT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', minWidth: 0 }}>
            
            {/* 1. ACCOUNT OVERVIEW CARD MATCHING PROFILE.PNG */}
            <div className="detail-card-padded" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '28px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              <h3 style={{ fontSize: '17px', fontWeight: 900, color: '#0f172a', margin: '0 0 20px 0' }}>
                Account Overview
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13.5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b' }}>
                    <Package size={17} color="#dc2626" />
                    <span>Total Orders</span>
                  </div>
                  <span style={{ fontWeight: 900, color: '#0f172a' }}>{orders.length}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13.5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b' }}>
                    <Heart size={17} color="#dc2626" />
                    <span>Wishlist Items</span>
                  </div>
                  <span style={{ fontWeight: 900, color: '#0f172a' }}>{wishlistCount}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13.5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b' }}>
                    <Calendar size={17} color="#dc2626" />
                    <span>Member Since</span>
                  </div>
                  <span style={{ fontWeight: 900, color: '#0f172a' }}>
                    {user && user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'Aug 2026'}
                  </span>
                </div>

              </div>
            </div>

            {/* 2. RECENT ORDERS CARD MATCHING PROFILE.PNG */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '28px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '17px', fontWeight: 900, color: '#0f172a', margin: 0 }}>
                  Recent Orders
                </h3>
                <Link
                  to="/orders"
                  style={{
                    color: '#dc2626',
                    fontSize: '13px',
                    fontWeight: 800,
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span>View All Orders</span>
                  <ChevronRight size={15} />
                </Link>
              </div>

              {/* RECENT ORDERS LIST */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {orders.length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#64748b', textAlign: 'center', padding: '16px 0' }}>
                    No recent orders placed yet.
                  </div>
                ) : (
                  orders.slice(0, 4).map((o) => {
                    const numStr = o.order_number || `#IR${o.id}`;
                    const amtStr = o.total_amount ? Number(o.total_amount).toFixed(2) : '0.00';
                    const dateStr = o.created_at ? new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent';
                    const itemsCount = Array.isArray(o.items) ? o.items.length : (o.items_count || 1);
                    const firstImg = (Array.isArray(o.items) && o.items[0]?.image_url) || o.image_url || getProductImageUrl('/uploads/panel_144led_pro.jpg');

                    return (
                      <div
                        key={o.id}
                        onClick={() => navigate('/orders')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          gap: '14px'
                        }}
                      >
                        <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                          <div style={{ width: '48px', height: '48px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px', flexShrink: 0 }}>
                            <img src={firstImg} alt="Panel" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                          </div>

                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                              <span style={{ fontSize: '13.5px', fontWeight: 900, color: '#0f172a' }}>Order {numStr}</span>
                              {renderStatusPill(o.order_status || 'Processing')}
                            </div>
                            <div style={{ fontSize: '11.5px', color: '#64748b' }}>
                              {dateStr} • {itemsCount} {itemsCount === 1 ? 'Item' : 'Items'}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '14.5px', fontWeight: 900, color: '#0f172a' }}>£{amtStr}</span>
                          <ChevronRight size={15} color="#94a3b8" />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

            </div>

          </div>

        </div>

      </div>

      {/* ADD ADDRESS MODAL */}
      {showAddAddressModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '520px', padding: '32px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', position: 'relative' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', margin: 0 }}>Add New Address</h3>
              <button onClick={() => setShowAddAddressModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
            </div>

            <form onSubmit={handleAddAddressSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Label (e.g. Home, Work)</label>
                  <input type="text" value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Recipient Full Name</label>
                  <input type="text" value={newAddr.full_name} onChange={(e) => setNewAddr({ ...newAddr, full_name: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} required />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Street Address</label>
                <input type="text" value={newAddr.street_address} onChange={(e) => setNewAddr({ ...newAddr, street_address: e.target.value })} placeholder="Flat / Building, Street Name" style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} required />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>City</label>
                  <input type="text" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Postal Code</label>
                  <input type="text" value={newAddr.postal_code} onChange={(e) => setNewAddr({ ...newAddr, postal_code: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} required />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Phone Number</label>
                <input type="text" value={newAddr.phone} onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} required />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                <button type="button" onClick={() => setShowAddAddressModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#ffffff', fontWeight: 800, fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#ffffff', fontWeight: 800, fontSize: '13px', cursor: 'pointer' }}>Save Address</button>
              </div>
            </form>

          </div>
        </div>
      )}

      {/* CHANGE PASSWORD MODAL */}
      {showPasswordModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '440px', padding: '32px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', margin: 0 }}>Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); alert("Password updated successfully!"); setShowPasswordModal(false); }} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Current Password</label>
                <input type="password" value={currentPass} onChange={(e) => setCurrentPass(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} required />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>New Password</label>
                <input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} required />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>Confirm New Password</label>
                <input type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }} required />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                <button type="button" onClick={() => setShowPasswordModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#ffffff', fontWeight: 800, fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#ffffff', fontWeight: 800, fontSize: '13px', cursor: 'pointer' }}>Update Password</button>
              </div>
            </form>

          </div>
        </div>
      )}

    </div>
  );
};

export default UserProfile;
